import React, { useCallback, useEffect, useRef, useState } from "react";
import { AdminModal, EmptyState, SkeletonLoader } from "../../components/admin";
import { ConfirmModal } from "../../components/common";
import AdminLayout from "../../components/layout/AdminLayout";
import { useToast } from "../../contexts/ToastContext";
import adminService, {
  AdminUniversity,
  CreateUniversityRequest
} from "../../services/api/adminService";

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow";

interface UniversityFormProps {
  onSubmit: (e: React.FormEvent) => void;
  formData: CreateUniversityRequest;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  isSubmitting: boolean;
  onClose: () => void;
  logoFile: File | null;
  bannerFile: File | null;
  onLogoFileChange: (file: File | null) => void;
  onBannerFileChange: (file: File | null) => void;
  onRemoveLogo?: () => void;
  onRemoveBanner?: () => void;
}

const UniversityForm: React.FC<UniversityFormProps> = ({
  onSubmit,
  formData,
  handleInputChange,
  isSubmitting,
  onClose,
  logoFile,
  bannerFile,
  onLogoFileChange,
  onBannerFileChange,
  onRemoveLogo,
  onRemoveBanner,
}) => {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onLogoFileChange(file);
    e.target.value = '';
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onBannerFileChange(file);
    e.target.value = '';
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            University Name <span className="text-red-400">*</span>
          </label>
          <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Short Name</label>
          <input type="text" name="shortName" value={formData.shortName || ""} onChange={handleInputChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Country <span className="text-red-400">*</span>
          </label>
          <input type="text" name="country" value={formData.country} onChange={handleInputChange} required className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            City <span className="text-red-400">*</span>
          </label>
          <input type="text" name="city" value={formData.city} onChange={handleInputChange} required className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Website</label>
          <input type="url" name="website" value={formData.website || ""} onChange={handleInputChange} placeholder="https://example.com" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
          <input type="email" name="email" value={formData.email || ""} onChange={handleInputChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
          <input type="tel" name="phone" value={formData.phone || ""} onChange={handleInputChange} className={inputClass} />
        </div>

        {/* Logo Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo Image</label>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {logoFile ? (
                <img src={URL.createObjectURL(logoFile)} alt="logo preview" className="w-full h-full object-cover" />
              ) : formData.logoUrl ? (
                <img src={formData.logoUrl} alt="logo" className="w-full h-full object-cover" />
              ) : (
                <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <input ref={logoInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleLogoChange} />
              <button type="button" onClick={() => logoInputRef.current?.click()} className="px-3 py-1.5 text-xs font-medium text-cyan-700 border border-cyan-300 rounded-lg hover:bg-cyan-50 transition-colors">
                {logoFile ? 'Change' : (formData.logoUrl ? 'Replace' : 'Choose File')}
              </button>
              {logoFile && (
                <button type="button" onClick={() => onLogoFileChange(null)} className="ml-2 text-xs text-red-500 hover:text-red-700 transition-colors">Cancel</button>
              )}
              {!logoFile && formData.logoUrl && onRemoveLogo && (
                <button type="button" onClick={onRemoveLogo} className="ml-2 text-xs text-red-500 hover:text-red-700 transition-colors">Remove current</button>
              )}
              <p className="mt-1 text-xs text-gray-400 truncate">
                {logoFile ? logoFile.name : (formData.logoUrl ? 'Current logo set' : 'No file selected · JPEG, PNG, WebP')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
        <input type="text" name="address" value={formData.address || ""} onChange={handleInputChange} className={inputClass} />
      </div>

      {/* Banner Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Banner Image <span className="text-xs text-gray-400 font-normal">(hero background on university page)</span>
        </label>
        <div className="flex items-start gap-3">
          <div className="w-36 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {bannerFile ? (
              <img src={URL.createObjectURL(bannerFile)} alt="banner preview" className="w-full h-full object-cover" />
            ) : formData.bannerUrl ? (
              <img src={formData.bannerUrl} alt="banner" className="w-full h-full object-cover" />
            ) : (
              <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <input ref={bannerInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={handleBannerChange} />
            <button type="button" onClick={() => bannerInputRef.current?.click()} className="px-3 py-1.5 text-xs font-medium text-cyan-700 border border-cyan-300 rounded-lg hover:bg-cyan-50 transition-colors">
              {bannerFile ? 'Change' : (formData.bannerUrl ? 'Replace' : 'Choose File')}
            </button>
            {bannerFile && (
              <button type="button" onClick={() => onBannerFileChange(null)} className="ml-2 text-xs text-red-500 hover:text-red-700 transition-colors">Cancel</button>
            )}
            {!bannerFile && formData.bannerUrl && onRemoveBanner && (
              <button type="button" onClick={onRemoveBanner} className="ml-2 text-xs text-red-500 hover:text-red-700 transition-colors">Remove current</button>
            )}
            <p className="mt-1 text-xs text-gray-400 truncate">
              {bannerFile ? bannerFile.name : (formData.bannerUrl ? 'Current banner set' : 'No file · JPEG, PNG, WebP · Recommended 1200×400')}
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
        <textarea name="description" value={formData.description || ""} onChange={handleInputChange} rows={3} className={`${inputClass} resize-none`} />
      </div>

      <div className="flex items-center gap-2">
        <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded" />
        <label className="text-sm text-gray-700">Active</label>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50">
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

const UniversityManagement: React.FC = () => {
  const [universities, setUniversities] = useState<AdminUniversity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState<AdminUniversity | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingUniversity, setDeletingUniversity] = useState<AdminUniversity | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [pendingLogoRemove, setPendingLogoRemove] = useState(false);
  const [pendingBannerRemove, setPendingBannerRemove] = useState(false);

  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState<CreateUniversityRequest>({
    name: "", shortName: "", description: "", website: "", email: "",
    phone: "", address: "", city: "", country: "", logoUrl: "", bannerUrl: "",
    isActive: true,
  });

  const fetchUniversities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getUniversities({
        page: currentPage, limit: 20, search: searchQuery || undefined,
      });
      if (response.data && "universities" in response.data && Array.isArray(response.data.universities)) {
        setUniversities(response.data.universities);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        setUniversities([]);
      }
    } catch (err: any) {
      showError("Failed to fetch universities");
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, showError]);

  useEffect(() => { fetchUniversities(); }, [fetchUniversities]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      if (type === "checkbox") {
        setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }, []
  );

  const resetForm = useCallback(() => {
    setFormData({
      name: "", shortName: "", description: "", website: "", email: "",
      phone: "", address: "", city: "", country: "", logoUrl: "", bannerUrl: "",
      isActive: true,
    });
    setLogoFile(null);
    setBannerFile(null);
    setPendingLogoRemove(false);
    setPendingBannerRemove(false);
  }, []);

  const handleCreateUniversity = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      // Strip image URL fields — images are managed by the upload endpoint
      const { logoUrl: _l, bannerUrl: _b, ...jsonData } = formData;
      const response = await adminService.createUniversity(jsonData);
      const universityId = response.data?.id;
      if (universityId) {
        if (logoFile) await adminService.uploadUniversityImage(universityId, logoFile, 'logo');
        if (bannerFile) await adminService.uploadUniversityImage(universityId, bannerFile, 'banner');
      }
      showSuccess("University created successfully");
      setShowCreateModal(false);
      resetForm();
      fetchUniversities();
    } catch (err: any) {
      showError(err.message || "Failed to create university");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUniversity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUniversity) return;
    try {
      setIsSubmitting(true);
      // Strip image URL fields — managed by upload endpoint; explicit removals pass empty string
      const { logoUrl: _l, bannerUrl: _b, ...baseData } = formData;
      const jsonData = {
        ...baseData,
        ...(pendingLogoRemove && { logoUrl: "" }),
        ...(pendingBannerRemove && { bannerUrl: "" }),
      };
      await adminService.updateUniversity(editingUniversity.id, jsonData);
      if (logoFile) await adminService.uploadUniversityImage(editingUniversity.id, logoFile, 'logo');
      if (bannerFile) await adminService.uploadUniversityImage(editingUniversity.id, bannerFile, 'banner');
      showSuccess("University updated successfully");
      setShowEditModal(false);
      setEditingUniversity(null);
      resetForm();
      fetchUniversities();
    } catch (err: any) {
      showError(err.message || "Failed to update university");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUniversity = (university: AdminUniversity) => {
    setDeletingUniversity(university);
    setShowDeleteModal(true);
  };

  const confirmDeleteUniversity = async () => {
    if (!deletingUniversity) return;
    setShowDeleteModal(false);
    try {
      await adminService.deleteUniversity(deletingUniversity.id);
      showSuccess("University deleted successfully");
      fetchUniversities();
    } catch (err: any) {
      showError(err.message || "Failed to delete university");
    } finally {
      setDeletingUniversity(null);
    }
  };

  const openEditModal = (university: AdminUniversity) => {
    setEditingUniversity(university);
    setFormData({
      name: university.name, shortName: university.shortName || "",
      description: university.description || "", website: university.website || "",
      email: university.email || "", phone: university.phone || "",
      address: university.address || "", city: university.city,
      country: university.country, logoUrl: university.logoUrl || "",
      bannerUrl: university.bannerUrl || "", isActive: university.isActive,
    });
    setLogoFile(null);
    setBannerFile(null);
    setPendingLogoRemove(false);
    setPendingBannerRemove(false);
    setShowEditModal(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Universities</h1>
            <p className="text-sm text-gray-500 mt-1">Manage universities in the platform</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors"
          >
            Add University
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Search</label>
            <input type="text" placeholder="Search by name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">University</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Location</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Contact</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Status</th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && universities.length === 0 ? (
                  <SkeletonLoader variant="table-row" count={5} columns={5} />
                ) : universities.length > 0 ? (
                  universities.map((university) => (
                    <tr key={university.id} className="border-b border-gray-50 hover:bg-cyan-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {university.logoUrl ? (
                            <img
                              className="h-9 w-9 rounded-lg object-cover"
                              src={university.logoUrl}
                              alt={university.name}
                              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                          ) : (
                            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-white">{university.name.charAt(0)}</span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{university.name}</p>
                            {university.shortName && <p className="text-xs text-gray-400">{university.shortName}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-700">{university.city}, {university.country}</p>
                        {university.address && <p className="text-xs text-gray-400 truncate max-w-[200px]">{university.address}</p>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-700">{university.email || "--"}</p>
                        <p className="text-xs text-gray-400">{university.phone || "--"}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ring-1 ring-inset ${university.isActive
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                          : "bg-red-50 text-red-700 ring-red-200"
                          }`}>
                          {university.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => openEditModal(university)} className="text-sm font-medium text-cyan-600 hover:text-cyan-800 transition-colors">
                            Edit
                          </button>
                          <button onClick={() => handleDeleteUniversity(university)} className="text-sm font-medium text-red-500 hover:text-red-700 transition-colors">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState title="No universities found" description="Add a new university or adjust your search." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Page {currentPage} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <AdminModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New University" size="lg">
        <UniversityForm onSubmit={handleCreateUniversity} formData={formData} handleInputChange={handleInputChange} isSubmitting={isSubmitting} onClose={() => setShowCreateModal(false)} logoFile={logoFile} bannerFile={bannerFile} onLogoFileChange={setLogoFile} onBannerFileChange={setBannerFile} />
      </AdminModal>

      {/* Edit Modal */}
      <AdminModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setEditingUniversity(null); resetForm(); }} title="Edit University" size="lg">
        <UniversityForm onSubmit={handleEditUniversity} formData={formData} handleInputChange={handleInputChange} isSubmitting={isSubmitting} onClose={() => { setShowEditModal(false); setEditingUniversity(null); resetForm(); }} logoFile={logoFile} bannerFile={bannerFile} onLogoFileChange={setLogoFile} onBannerFileChange={setBannerFile}
          onRemoveLogo={() => {
            setFormData(prev => ({ ...prev, logoUrl: "" }));
            setPendingLogoRemove(true);
            setLogoFile(null);
          }}
          onRemoveBanner={() => {
            setFormData(prev => ({ ...prev, bannerUrl: "" }));
            setPendingBannerRemove(true);
            setBannerFile(null);
          }}
        />
      </AdminModal>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete University"
        message={`Are you sure you want to delete "${deletingUniversity?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteUniversity}
        onCancel={() => { setShowDeleteModal(false); setDeletingUniversity(null); }}
        variant="danger"
      />
    </AdminLayout>
  );
};

export default UniversityManagement;
